'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { IPlace, useUpFetch } from '@repo/sdk'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Sheet,
  SheetContent,
  SheetTrigger,
  Spinner,
} from '@repo/ui'
import { toast } from '@repo/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

interface Props {
  address: IPlace
  companyId?: number
  id: number
}

const schema = z.object({
  city: z.string(),
  name: z.string(),
  post_code: z.string(),
  street: z.string(),
})

type schemaType = z.infer<typeof schema>

export const EditAddress = ({ address, companyId, id }: Props) => {
  const [open, setOpen] = useState(false)

  const queryClient = useQueryClient()
  const upFetch = useUpFetch()

  const {
    isPending: editingAddress,
    mutate: editAddress,
    status,
  } = useMutation({
    mutationFn: async (data: schemaType) => {
      return upFetch(`/companies/${companyId}/address-book/${id}`, {
        body: data,
        method: 'PATCH',
      })
    },
  })

  const form = useForm<schemaType>({
    defaultValues: {
      post_code: address.postcode,
      street: address.place_name,
      ...address,
      name: address.address_book?.name,
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (!editingAddress && status === 'success') {
      queryClient.invalidateQueries({ queryKey: ['/companies/address-book'] })
      toast.success('Address edited')
      setOpen(false)
    }
  }, [editingAddress, status])

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>
        <Button variant={'ghost'}>Edit</Button>
      </SheetTrigger>
      <SheetContent className={'z-[1401]'} side={'left'}>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(data => {
              editAddress(data)
            })}
          >
            <FormField
              control={form.control}
              name={'name'}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'street'}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'post_code'}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Post code</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'city'}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              onClick={() => {
                setOpen(false)
              }}
              variant={'ghost'}
            >
              Cancel
            </Button>
            <Button type={'submit'}>{editingAddress ? <Spinner /> : 'Save'}</Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
